import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { WalletConnectPanel } from "@/components/wallet-connect-panel";
import { useWalletState } from "@/contexts/wallet-context";
import { Button } from "@/components/ui/button";
import { GBUX, FLEET } from "@shared/fleet";
import { WEB3 } from "@/lib/web3-ssot";
import { PHANTOM_SSOT } from "@/lib/phantom-browser";
import { BRAND } from "@shared/brand";
import { isLoggedIn, loginWithGrudgeId } from "@/lib/grudge-auth";
import { ExternalLink, Loader2, LogIn, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  isMobileBrowser,
  isInPhantomInAppBrowser,
  openDappInPhantomBrowser,
  phantomDappUrl,
} from "@/lib/mobile-web3";

export default function ConnectWallet() {
  const { wallet, connect } = useWalletState();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (wallet.connected && wallet.publicKey) {
      const t = setTimeout(() => setLocation("/lobby"), 400);
      return () => clearTimeout(t);
    }
  }, [wallet.connected, wallet.publicKey, setLocation]);

  // Phantom OAuth return — template checkExistingSession (handled also in WalletProvider).
  // Keep local toast when session lands.
  useEffect(() => {
    if (!wallet.connected || !wallet.publicKey) return;
    const pending = sessionStorage.getItem("phantom_oauth_pending");
    if (pending === "1") {
      try {
        sessionStorage.removeItem("phantom_oauth_pending");
      } catch {
        /* ignore */
      }
      toast({
        title: "Phantom connected",
        description: `${wallet.publicKey.slice(0, 4)}… · mainnet`,
      });
    }
  }, [wallet.connected, wallet.publicKey, toast]);

  const phantomConnect = async (mode: "injected" | "google" | "apple") => {
    setBusy(true);
    try {
      if (mode !== "injected") {
        try {
          sessionStorage.setItem("phantom_oauth_pending", "1");
        } catch {
          /* ignore */
        }
      }
      await connect("phantom", { phantomMode: mode });
      toast({ title: "Phantom connected", description: "Solana mainnet" });
    } catch (e) {
      // Template: OAuth redirects away — not a failure
      if (e instanceof Error && e.message === "REDIRECT_IN_PROGRESS") {
        toast({
          title: "Continue in Phantom / Google / Apple",
          description: "You will return to /connect after sign-in",
        });
        return;
      }
      toast({
        title: "Phantom connect failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #120636 0%, #06091a 55%, #000 100%)",
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${BRAND.pokerSolPro})`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
          opacity: 0.28,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,9,26,0.55) 0%, rgba(0,0,0,0.88) 100%)",
        }}
      />
      <div className="max-w-md w-full relative z-10 space-y-4">
        <div className="text-center mb-2">
          <h1 className="text-3xl font-bold text-white mb-2">
            Sign in · connect · play
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            <strong className="text-violet-200">Grudge ID</strong> who you are ·{" "}
            <strong className="text-fuchsia-200">Phantom</strong> how you sign ·{" "}
            <strong className="text-emerald-300">play ledger</strong> how the
            table runs
          </p>
        </div>

        <div className="rounded-xl border border-sky-500/30 bg-sky-950/25 p-3 space-y-2">
          <p className="text-xs font-semibold text-sky-100">
            1 · Grudge ID
          </p>
          {isLoggedIn() ? (
            <p className="text-xs text-emerald-400">Signed in</p>
          ) : (
            <Button
              className="w-full bg-sky-700 hover:bg-sky-600"
              onClick={() => loginWithGrudgeId("/connect")}
            >
              <LogIn className="mr-2 h-4 w-4" />
              Sign in with Grudge ID
            </Button>
          )}
          <p className="text-[10px] text-zinc-500">
            id.grudge-studio.com — one studio account. Not a wallet.
          </p>
        </div>

        <div className="rounded-xl border border-violet-500/35 bg-violet-950/30 p-3 space-y-2">
          <p className="text-xs font-semibold text-violet-200">
            2 · Phantom Connect
          </p>
          {isMobileBrowser() && !isInPhantomInAppBrowser() && (
            <Button
              className="w-full h-12 bg-violet-600 hover:bg-violet-500 font-bold"
              disabled={busy}
              onClick={() =>
                openDappInPhantomBrowser(phantomDappUrl("/connect"))
              }
            >
              <Smartphone className="mr-2 h-5 w-5" />
              Open full-screen in Phantom app
            </Button>
          )}
          <Button
            className="w-full bg-violet-700 hover:bg-violet-600"
            disabled={busy}
            onClick={() => void phantomConnect("injected")}
          >
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {isInPhantomInAppBrowser()
              ? "Connect Phantom wallet"
              : "Phantom extension / inject"}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              className="border-zinc-700"
              disabled={busy}
              onClick={() => void phantomConnect("google")}
            >
              Google
            </Button>
            <Button
              variant="outline"
              className="border-zinc-700"
              disabled={busy}
              onClick={() => void phantomConnect("apple")}
            >
              Apple
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            Extension, or Google / Apple embedded. Same Phantom Connect SDK.
            Redirect{" "}
            <code className="text-zinc-400">{PHANTOM_SSOT.redirectUrl}</code>
          </p>
        </div>

        <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/25 p-3 space-y-1">
          <p className="text-xs font-semibold text-emerald-200">
            3 · Play ledger
          </p>
          <p className="text-[11px] text-emerald-100/90 leading-relaxed">
            Server-side GBUX that sits tables, slots, BJ.{" "}
            <strong>200 real GBUX</strong> on first player-wallet connect.
            More via Phantom SPL deposit.
          </p>
          {wallet.connected && wallet.publicKey ? (
            <p className="text-sm text-emerald-300 font-semibold tabular-nums">
              {Number(wallet.playGbux || wallet.gbuxBalance || 0).toLocaleString()}{" "}
              play GBUX
            </p>
          ) : (
            <p className="text-[11px] text-zinc-500">
              Connect Phantom to open the server wallet.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-zinc-800 bg-black/30 px-3 py-2 text-[11px] text-zinc-400 flex flex-wrap gap-x-3 gap-y-1 justify-center">
          <span>{WEB3.network}</span>
          <span>mint {GBUX.mint.slice(0, 6)}…</span>
          <a
            className="inline-flex items-center gap-1 text-cyan-300 underline"
            href={WEB3.explorerToken(GBUX.mint)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Solscan <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <WalletConnectPanel />
        <Button
          variant="ghost"
          className="w-full text-zinc-400"
          onClick={() => setLocation("/lobby")}
        >
          Continue to lobby
        </Button>
        <p className="text-center text-[10px] text-zinc-600">
          <a className="underline" href="/tos">
            Terms
          </a>
          {" · "}
          <a className="underline" href="/privacy">
            Privacy
          </a>
          {" · "}
          <a className="underline" href={FLEET.supportMailto}>
            {FLEET.supportEmail}
          </a>
          {" · "}
          <a
            className="underline"
            href={FLEET.walletSite}
            target="_blank"
            rel="noopener noreferrer"
          >
            Studio bag
          </a>
        </p>
      </div>
    </div>
  );
}
